import React, { memo, useMemo } from 'react';
import Image from 'next/image';
import { twMerge } from 'tailwind-merge';
import { FileIcon, FileText, Image as ImageIcon, Music, Video, File, TableIcon } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi
} from "@/components/ui/carousel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { FileIconType } from '@/lib/icons';
import type { BaseCustomProps } from './types';
import FirebaseService from '@/lib/firebase';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

// In-memory cache to prevent duplicate API calls
const urlCache = new Map<string, Promise<string>>();
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

// Helper function to check if a URL is a known public domain (no network requests)
const isKnownPublicDomain = (url: string): boolean => {
  try {
    const hostname = new URL(url).hostname;
    const publicDomains = [
      'localhost',
      'sunholo.com',
      'example.com',
      'httpbin.org',
      'jsonplaceholder.typicode.com'
    ];
    return publicDomains.includes(hostname);
  } catch (error) {
    return false;
  }
};

// Helper function to convert HTTPS Google Cloud Storage URLs to gs:// format
const convertHttpsToGsUri = (httpsUrl: string): string | null => {
  try {
    const url = new URL(httpsUrl);
    
    // Pattern 1: storage.googleapis.com format
    // https://storage.googleapis.com/BUCKET_NAME/PATH
    if (url.hostname === 'storage.googleapis.com') {
      const pathParts = url.pathname.split('/').filter(Boolean);
      if (pathParts.length >= 1) {
        const bucket = pathParts[0];
        const path = pathParts.slice(1).join('/');
        const decodedPath = decodeURIComponent(path);
        return `gs://${bucket}/${decodedPath}`;
      }
    }
    
    // Pattern 2: storage.cloud.google.com format
    // https://storage.cloud.google.com/BUCKET_NAME/PATH
    if (url.hostname === 'storage.cloud.google.com') {
      const pathParts = url.pathname.split('/').filter(Boolean);
      if (pathParts.length >= 1) {
        const bucket = pathParts[0];
        const path = pathParts.slice(1).join('/');
        const decodedPath = decodeURIComponent(path);
        return `gs://${bucket}/${decodedPath}`;
      }
    }
    
    // Pattern 3: BUCKET_NAME.storage.googleapis.com format  
    // https://BUCKET_NAME.storage.googleapis.com/PATH
    if (url.hostname.endsWith('.storage.googleapis.com')) {
      const bucket = url.hostname.replace('.storage.googleapis.com', '');
      // Ensure we don't have double .storage.googleapis.com in bucket name
      const cleanBucket = bucket.replace('.storage.googleapis.com', '');
      const path = url.pathname.slice(1); // Remove leading slash
      const decodedPath = decodeURIComponent(path);
      return `gs://${cleanBucket}/${decodedPath}`;
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing HTTPS URL:', error);
    return null;
  }
};

// Helper to get assistant context from window (set by parent component)
const getAssistantContext = () => {
  // Try to get from window object (set by parent component with assistant context)
  if (typeof window !== 'undefined' && (window as any)._assistantContext) {
    return (window as any)._assistantContext;
  }
  return {
    assistantId: 'unknown',
    assistantName: 'Unknown Assistant',
    assistantAvatar: null
  };
};

// Helper function to track direct preview (when copy fails or not attempted)
const trackDirectPreview = async (
  uri: string,
  fileType: string,
  fileName: string
): Promise<void> => {
  try {
    const user = FirebaseService.auth.currentUser;
    if (!user) return;

    const idToken = await user.getIdToken();
    const assistantContext = getAssistantContext();

    await fetch('/api/user/previews', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        sourceUri: uri,
        fileName: fileName || uri.split('/').pop() || 'unknown',
        fileType,
        activityType: 'preview',
        assistantId: assistantContext.assistantId,
        assistantName: assistantContext.assistantName,
        assistantAvatar: assistantContext.assistantAvatar,
        conversationUrl: `/assistant/${assistantContext.assistantId}`,
        copySucceeded: false
      })
    });

    console.log('✅ Tracked direct preview:', fileName);
  } catch (error) {
    console.error('Failed to track direct preview:', error);
    // Don't throw - tracking failures shouldn't break preview functionality
  }
};

// Helper function to copy any file to user's Firebase storage
const copyFileToUserStorage = async (uri: string): Promise<string | null> => {
  console.log('🔄 Starting copy process for URI:', uri);

  try {
    // Get the current user's auth token
    const user = FirebaseService.auth.currentUser;
    if (!user) {
      console.error('❌ No authenticated user for file copy');
      return null;
    }

    console.log('📝 Getting ID token for user:', user.email);
    const idToken = await user.getIdToken();
    const assistantContext = getAssistantContext();

    // Convert HTTPS GCS URLs to gs:// format for the API
    let gcsUri = uri;
    if (uri.startsWith('https://') && (uri.includes('storage.googleapis.com') || uri.includes('storage.cloud.google.com'))) {
      const convertedUri = convertHttpsToGsUri(uri);
      if (convertedUri) {
        gcsUri = convertedUri;
        console.log('🔄 Converted HTTPS to gs:// format:', { original: uri, converted: gcsUri });
      }
    }

    // Skip copy attempt if the URI is already malformed
    if (gcsUri.includes('.storage.googleapis.com') && gcsUri.startsWith('gs://')) {
      console.log('⚠️ Skipping copy due to malformed gs:// URI, using direct fallback');
      return null; // Force fallback to direct URL approach
    }

    console.log('📡 Sending copy request to API...');
    const response = await fetch('/api/gcs/copy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        gcsUri,
        assistantId: assistantContext.assistantId,
        assistantName: assistantContext.assistantName,
        assistantAvatar: assistantContext.assistantAvatar
      }),
    });

    console.log('📨 API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ Failed to copy file:', {
        status: response.status,
        statusText: response.statusText,
        ...errorData,
        originalUri: uri,
        processedUri: gcsUri,
        attemptedBy: errorData.userEmail || 'unknown user',
        serviceAccount: errorData.serviceAccount || 'unknown service account'
      });
      return null;
    }

    const data = await response.json();
    console.log('✅ Successfully copied file to user storage:', data);

    // Generate authenticated download URL using Firebase Storage SDK
    try {
      const storage = getStorage();
      const storageRef = ref(storage, data.destPath);
      const downloadUrl = await getDownloadURL(storageRef);
      console.log('🔐 Generated authenticated download URL:', downloadUrl);
      return downloadUrl;
    } catch (urlError) {
      console.error('❌ Failed to generate authenticated URL, using API URL:', urlError);
      return data.publicUrl;
    }
  } catch (error) {
    console.error('❌ Error copying file:', error);
    return null;
  }
};

// Simplified function to get a displayable URL with copy-first approach and caching
const getDisplayableUrl = async (uri: string): Promise<string> => {
  // Check cache first
  if (urlCache.has(uri)) {
    console.log('🎯 Using cached URL for:', uri);
    return await urlCache.get(uri)!;
  }

  console.log('🔍 Processing URI for display:', uri);
  
  // Create a promise and cache it immediately to prevent duplicate calls
  const processPromise = processUri(uri);
  urlCache.set(uri, processPromise);
  
  // Set cache expiry
  setTimeout(() => {
    urlCache.delete(uri);
  }, CACHE_EXPIRY);
  
  return await processPromise;
};

// Internal function to process URIs (copy-first with proper working fallback)
const processUri = async (uri: string): Promise<string> => {
  // Clean up malformed gs:// URIs that include .storage.googleapis.com in bucket name
  let cleanUri = uri;
  if (uri.startsWith('gs://') && uri.includes('.storage.googleapis.com')) {
    cleanUri = uri.replace('.storage.googleapis.com', '');
    console.log('🧹 Cleaned malformed gs:// URI:', { original: uri, cleaned: cleanUri });
  }
  
  // For known public domains, use directly
  if (cleanUri.startsWith('https://') && isKnownPublicDomain(cleanUri)) {
    console.log('✅ Using known public domain directly:', cleanUri);
    return cleanUri;
  }

  // For GCS files, try copy first, then fallback to direct (working) approach
  const isGcsFile = cleanUri.startsWith('gs://') || 
                   (cleanUri.startsWith('https://') && (
                     cleanUri.includes('storage.googleapis.com') || 
                     cleanUri.includes('storage.cloud.google.com')
                   ));

  if (isGcsFile) {
    console.log('🔄 GCS file detected, trying copy first:', cleanUri);

    // Try copying first (use cleaned URI)
    const copiedUrl = await copyFileToUserStorage(cleanUri);
    if (copiedUrl) {
      console.log('✅ Successfully copied to user storage:', copiedUrl);
      return copiedUrl;
    }

    // If copy fails, track as direct preview and use fallback
    console.log('❌ Copy failed, using working direct URL fallback');

    // Infer file type from URI
    const fileType = cleanUri.match(/\.(pdf|png|jpg|jpeg|gif|csv|txt|mp4|webm|mp3)$/i)?.[1] || 'unknown';
    const fileName = cleanUri.split('/').pop() || 'unknown';
    const mimeType = fileType === 'pdf' ? 'application/pdf' :
                     fileType.match(/png|jpg|jpeg|gif/) ? `image/${fileType}` :
                     fileType === 'csv' ? 'text/csv' :
                     fileType.match(/mp4|webm/) ? `video/${fileType}` :
                     fileType.match(/mp3|wav/) ? `audio/${fileType}` :
                     'application/octet-stream';

    // Track direct preview (non-blocking)
    trackDirectPreview(cleanUri, mimeType, fileName).catch(err =>
      console.error('Failed to track direct preview:', err)
    );
    
    if (cleanUri.startsWith('gs://')) {
      const match = cleanUri.match(/^gs:\/\/([^\/]+)\/(.+)$/);
      if (match) {
        const [, bucket, path] = match;
        
        // Check if this is a Firebase Storage bucket
        if (bucket.includes('firebasestorage.app')) {
          // Firebase Storage URLs need to use firebasestorage.googleapis.com
          const projectId = bucket.replace('.firebasestorage.app', '');
          const encodedPath = encodeURIComponent(path).replace(/%2F/g, '/');
          const directUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
          console.log('✅ Using Firebase Storage URL conversion:', directUrl);
          return directUrl;
        } else {
          // Regular GCS bucket
          const encodedPath = encodeURIComponent(path).replace(/%2F/g, '/');
          const directUrl = `https://storage.cloud.google.com/${bucket}/${encodedPath}`;
          console.log('✅ Using direct gs:// conversion fallback:', directUrl);
          return directUrl;
        }
      }
    } else if (cleanUri.includes('storage.googleapis.com')) {
      // Convert https://BUCKET.storage.googleapis.com/PATH or https://storage.googleapis.com/BUCKET/PATH
      // to https://storage.cloud.google.com/BUCKET/PATH
      
      const url = new URL(cleanUri);
      let bucket = '';
      let path = '';
      
      if (url.hostname.endsWith('.storage.googleapis.com')) {
        // Format: https://BUCKET.storage.googleapis.com/PATH
        bucket = url.hostname.replace('.storage.googleapis.com', '');
        path = url.pathname.slice(1); // Remove leading slash
      } else if (url.hostname === 'storage.googleapis.com') {
        // Format: https://storage.googleapis.com/BUCKET/PATH
        const pathParts = url.pathname.split('/').filter(Boolean);
        bucket = pathParts[0];
        path = pathParts.slice(1).join('/');
      }
      
      const directUrl = `https://storage.cloud.google.com/${bucket}/${path}`;
      console.log('✅ Using direct googleapis→cloud conversion fallback:', directUrl);
      return directUrl;
    } else if (cleanUri.includes('storage.cloud.google.com')) {
      console.log('✅ Using original cloud.google.com URL as fallback:', cleanUri);
      return cleanUri;
    }
  }

  // For external URLs, use original URL directly
  if (cleanUri.startsWith('https://') || cleanUri.startsWith('http://')) {
    console.log('✅ Using external URL directly:', cleanUri);
    return cleanUri;
  }

  // Default: return cleaned URI
  console.log('🔄 Using cleaned URI:', cleanUri);
  return cleanUri;
};

// Helper function to parse CSV data
const parseCSV = (csvText: string): { headers: string[], rows: string[][] } => {
  const lines = csvText.trim().split('\n');
  if (lines.length === 0) return { headers: [], rows: [] };
  
  // Detect delimiter - check first line for semicolons vs commas
  const firstLine = lines[0];
  const semicolonCount = (firstLine.match(/;/g) || []).length;
  const commaCount = (firstLine.match(/,/g) || []).length;
  const delimiter = semicolonCount > commaCount ? ';' : ',';
  
  // CSV parser - handles both comma and semicolon separated values with quoted strings
  const parseRow = (row: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      
      if (char === '"') {
        if (inQuotes && row[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i++;
        } else {
          // Toggle quotes
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        // End of field
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last field
    result.push(current.trim());
    return result;
  };
  
  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(line => parseRow(line));
  
  // Filter out empty rows (rows where all cells are empty)
  const nonEmptyRows = rows.filter(row => row.some(cell => cell.length > 0));
  
  return { headers, rows: nonEmptyRows };
};

interface PreviewItem {
  uri: string;
  type: string;
  name?: string;
}

interface PreviewProps extends BaseCustomProps {
  uri?: string;
  src?: string; // Support both uri and src props for convenience
  url?: string; // Support url prop as well
  type?: string;
  name?: string;
  items?: PreviewItem[];
}

interface PreviewContentProps extends PreviewItem {
  className?: string;
}

const ALLOWED_IMAGE_DOMAINS = [
  'localhost',
  'sunholo.com',
  'googleapis.com',
  'firebasestorage.googleapis.com',
  'storage.googleapis.com',
  'storage.cloud.google.com',
  'lh3.googleusercontent.com'
  // Add other allowed domains here
];

// Helper function to check if an image URL is from an allowed domain
const isAllowedImageDomain = (url: string): boolean => {
  try {
    const hostname = new URL(url).hostname;
    return ALLOWED_IMAGE_DOMAINS.includes(hostname);
  } catch (e) {
    return false;
  }
}

const PreviewContent = memo(({ uri, type, name, className }: PreviewContentProps) => {
    // All hooks at the top level, before any conditionals
    const [error, setError] = React.useState<string | null>(null);
    const [loaded, setLoaded] = React.useState(false);
    const [signedUrl, setSignedUrl] = React.useState<string>(uri);
    const [isConverting, setIsConverting] = React.useState(false);
    const [showDirectLink, setShowDirectLink] = React.useState(false);
    const [iframeLoaded, setIframeLoaded] = React.useState(false);
    const [csvData, setCsvData] = React.useState<{ headers: string[], rows: string[][] } | null>(null);
    const [isLoadingCsv, setIsLoadingCsv] = React.useState(false);

    // Convert URIs to displayable URLs using copy-first approach
    React.useEffect(() => {
      const convertUri = async () => {
        setIsConverting(true);
        setError(null); // Clear any previous errors
        try {
          const displayableUrl = await getDisplayableUrl(uri);
          setSignedUrl(displayableUrl);
        } catch (error) {
          console.error('Failed to get displayable URL:', {
            uri: uri,
            type: type,
            name: name,
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
          });
          // Still set the URL even if optimal conversion failed
          // The processUri function should return a fallback URL
          try {
            const fallbackUrl = await processUri(uri);
            setSignedUrl(fallbackUrl);
          } catch (fallbackError) {
            console.error('Even fallback URL generation failed:', fallbackError);
            setSignedUrl(uri); // Last resort - use original URI
          }
        } finally {
          setIsConverting(false);
        }
      };

      convertUri();
    }, [uri, type, name]);
    
    // Fetch and parse CSV data when type is text/csv
    React.useEffect(() => {
      if (type === 'text/csv' && signedUrl && !isConverting && !error) {
        const fetchCsvData = async () => {
          setIsLoadingCsv(true);
          try {
            const response = await fetch(signedUrl);
            if (!response.ok) {
              if (response.status === 403) {
                throw new Error('Access denied. The file may require authentication.');
              } else if (response.status === 404) {
                throw new Error('File not found.');
              } else {
                throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
              }
            }
            const text = await response.text();
            const parsedData = parseCSV(text);
            setCsvData(parsedData);
          } catch (error) {
            console.error('Failed to fetch/parse CSV:', {
              error,
              url: signedUrl,
              originalUri: uri
            });
            
            // For CORS errors or network failures, don't set an error - let the fallback handle it gracefully
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
              console.log('CSV fetch blocked by CORS - showing fallback link instead of error');
              // Don't set error - this will trigger the nice fallback UI
            } else if (error instanceof Error) {
              setError(error.message);
            } else {
              setError('Unable to load CSV file. It may be too large or have invalid format.');
            }
          } finally {
            setIsLoadingCsv(false);
          }
        };
        
        fetchCsvData();
      }
    }, [type, signedUrl, isConverting, error, uri]);
  
    const containerClasses = twMerge(
      "inline-block w-full",
      "rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden",
      "bg-gray-50 dark:bg-gray-900",
      className
    );
  
    const fallbackClasses = twMerge(
      "inline-flex flex-col items-center justify-center p-8",
      "text-gray-500 dark:text-gray-400"
    );
  
    // Helper function to render content based on type
    const renderContent = () => {
      // Show loading state while converting GCS URIs
      if (isConverting) {
        return (
          <div className={fallbackClasses}>
            <FileIcon className="w-12 h-12 mb-2 animate-pulse" />
            <span>Loading file...</span>
            {uri.startsWith('gs://') && (
              <span className="text-xs text-gray-500 mt-1">Preparing secure preview...</span>
            )}
          </div>
        );
      }

      // Show error state if conversion failed
      if (error) {
        return (
          <div className={fallbackClasses}>
            <FileIcon className="w-12 h-12 mb-2 text-red-500" />
            <span className="text-red-600 font-medium">Preview Error</span>
            <span className="text-sm text-gray-600 mt-2 text-center max-w-md">{error}</span>
            {signedUrl && (
              <a 
                href={signedUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <FileIcon className="w-4 h-4 mr-2" />
                Open File
              </a>
            )}
            <div className="text-xs text-gray-500 mt-2 max-w-md break-all">
              <div>Original: {uri}</div>
              {signedUrl && signedUrl !== uri && (
                <div className="mt-1">Attempted: {signedUrl}</div>
              )}
            </div>
          </div>
        );
      }

      if (type.startsWith('image/')) {
        // With copy-first approach, we only need to check if URL is allowed for direct display
        const isAllowedDomain = isAllowedImageDomain(signedUrl);

        if (!isAllowedDomain) {
          let hostname = '';
          try {
            hostname = new URL(signedUrl).hostname;
          } catch (e) {
            hostname = 'Invalid URL';
          }

          return (
            <div className={fallbackClasses}>
              <ImageIcon className="w-12 h-12 mb-2 text-orange-500" />
              <span className="text-center font-medium text-orange-600">Image preview blocked</span>
              <div className="mt-2 text-sm text-gray-600 max-w-md text-center">
                <div className="break-all">URL: {signedUrl}</div>
                <div className="mt-1">Domain &quot;{hostname}&quot; is not authorized</div>
                <div className="mt-2 font-medium">Contact support@aitana.chat to allow this domain</div>
              </div>
            </div>
          );
        }

        return (
          <div className="relative w-full">
            {!error ? (
              <div className="relative w-full h-[800px]">
                <Image
                  src={signedUrl}
                  alt={name || ''}
                  fill
                  className="object-contain"
                  onError={(e) => {
                    console.error('Image preview error:', e);
                    console.error('Image error details:', {
                      url: signedUrl,
                      type: type,
                      name: name,
                      // For Next.js Image component, the error details may be different
                      nativeEvent: e.nativeEvent
                    });
                    setError('Image preview unavailable. Please contact support@aitana.chat if this persists.');
                  }}
                />
              </div>
            ) : (
              <div className={fallbackClasses}>
                <ImageIcon className="w-12 h-12 mb-2 text-red-500" />
                <span className="text-red-600 font-medium">Preview Error</span>
                <span className="text-sm text-gray-600 mt-2 text-center">{error}</span>
                <div className="text-xs text-gray-500 mt-2 max-w-md break-all">
                  <span className="font-medium">URL:</span> {signedUrl}
                </div>
              </div>
            )}
          </div>
        );
      }
  
      if (type.startsWith('audio/')) {
        return (
          <div className="w-full p-4">
            {!error ? (
              <audio
                controls
                preload="metadata"
                className={twMerge(
                  "w-full",
                  !loaded && "invisible h-0"
                )}
                onError={(e) => {
                  console.error('Audio preview error:', e);
                  console.error('Audio error details:', {
                    url: signedUrl,
                    type: type,
                    name: name,
                    error: e.currentTarget.error
                  });
                  setError('Unable to play audio file. Please contact support@aitana.chat for assistance.');
                }}
                onLoadedMetadata={() => setLoaded(true)}
              >
                <source src={signedUrl} type={type} />
              </audio>
            ) : (
              <div className={fallbackClasses}>
                <Music className="w-12 h-12 mb-2 text-red-500" />
                <span className="text-red-600 font-medium">Preview Error</span>
                <span className="text-sm text-gray-600 mt-2 text-center">{error}</span>
                <div className="text-xs text-gray-500 mt-2 max-w-md break-all">
                  <span className="font-medium">URL:</span> {signedUrl}
                </div>
              </div>
            )}
  
            {!loaded && !error && (
              <div className={fallbackClasses}>
                <Music className="w-12 h-12 mb-2 animate-pulse" />
                <span>Loading audio...</span>
              </div>
            )}
          </div>
        );
      }
  
      if (type.startsWith('video/')) {
        return (
          <>
            {!error ? (
              <video 
                controls 
                className="w-full max-h-[600px]"
                onError={(e) => {
                  console.error('Video preview error:', e);
                  console.error('Video error details:', {
                    url: signedUrl,
                    type: type,
                    name: name,
                    error: e.currentTarget.error
                  });
                  setError('Video preview unavailable. Please contact support@aitana.chat if this persists.');
                }}
              >
                <source src={signedUrl} type={type} />
              </video>
            ) : (
              <div className={fallbackClasses}>
                <Video className="w-12 h-12 mb-2 text-red-500" />
                <span className="text-red-600 font-medium">Preview Error</span>
                <span className="text-sm text-gray-600 mt-2 text-center">{error}</span>
                <div className="text-xs text-gray-500 mt-2 max-w-md break-all">
                  <span className="font-medium">URL:</span> {signedUrl}
                </div>
              </div>
            )}
          </>
        );
      }
  
      if (type === 'application/pdf') {
        return (
          <div className="relative w-full">
            {!error && !showDirectLink && (
              <iframe
                src={signedUrl || 'about:blank'}
                title={`PDF Preview - ${name || 'Document'}`}
                className="w-full h-[800px]"
                style={{
                  minHeight: '800px',
                  maxHeight: '1200px'
                }}
                onError={(e) => {
                  console.error('PDF preview iframe error:', e);
                  console.error('Failed to load PDF URL:', signedUrl);
                  setError('Unable to load PDF. The file may not exist, be corrupted, or may have access restrictions.');
                }}
                onLoad={(e) => {
                  console.log('📄 PDF iframe onLoad triggered');
                  setIframeLoaded(true);
                  
                  // Give PDF time to load, but be more responsive to actual failures
                  setTimeout(() => {
                    try {
                      const iframe = e.target as HTMLIFrameElement;
                      const doc = iframe.contentDocument || iframe.contentWindow?.document;
                      
                      // Check if we can access the iframe content
                      if (doc) {
                        const body = doc.body;
                        // Look for clear error indicators
                        if (body && body.innerHTML.includes('blocked')) {
                          console.log('📄 PDF iframe shows blocked content');
                          setShowDirectLink(true);
                          setError(null);
                        } else if (body && body.innerHTML.includes('403')) {
                          console.log('📄 PDF iframe shows 403 error - showing fallback');
                          setShowDirectLink(true);
                          setError(null);
                        } else {
                          console.log('📄 PDF iframe appears to be loading normally - hiding fallback');
                          setShowDirectLink(false);
                          setError(null);
                        }
                      } else {
                        // Cross-origin access blocked - this is normal for external PDFs
                        // Check if it's a Firebase Storage URL (which should work via authenticated URLs)
                        const iframe = e.target as HTMLIFrameElement;
                        if (iframe.src.includes('firebasestorage.googleapis.com') && iframe.src.includes('token=')) {
                          console.log('📄 Firebase Storage authenticated URL detected - should load properly');
                          setShowDirectLink(false);
                          setError(null);
                        } else {
                          console.log('📄 PDF iframe cross-origin (normal) - continuing with iframe');
                        }
                      }
                    } catch (err) {
                      if (err instanceof DOMException && err.name === 'SecurityError') {
                        console.log('📄 SecurityError (expected for cross-origin PDFs) - checking URL type');
                        // For SecurityError, check URL type to determine if we should show fallback
                        const iframe = e.target as HTMLIFrameElement;
                        if (iframe.src.includes('firebasestorage.googleapis.com') && iframe.src.includes('token=')) {
                          console.log('📄 Firebase Storage authenticated URL - should work, hiding fallback');
                          setShowDirectLink(false);
                          setError(null);
                        } else if (iframe.src.includes('firebasestorage.googleapis.com') && iframe.src.includes('alt=media')) {
                          console.log('📄 Firebase Storage URL detected, but may need auth token');
                          setShowDirectLink(true);
                          setError(null);
                        } else {
                          console.log('📄 External PDF - checking if it needs fallback');
                          // Wait a bit longer to see if external content loads
                          // Don't show fallback for GCS URLs that are working
                          // The PDF is loading fine, just can't be inspected due to CORS
                        }
                      } else {
                        console.error('📄 Unexpected PDF loading error:', err);
                        setError('Unable to display PDF preview. Please contact support@aitana.chat for assistance.');
                      }
                    }
                  }, 1500);
                }}
              />
            )}
            
            {(showDirectLink || error) && (
              <div className={fallbackClasses}>
                {showDirectLink ? (
                  <>
                    <FileText className="w-12 h-12 mb-2 text-blue-500" />
                    <span className="text-blue-600 font-medium">PDF Available</span>
                    <span className="text-sm text-gray-600 mt-2 text-center">
                      This PDF cannot be previewed inline due to security restrictions, but you can view it directly.
                    </span>
                    <a 
                      href={signedUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Open PDF
                    </a>
                    <div className="text-xs text-gray-500 mt-2 break-all">
                      {name || 'Document'}
                    </div>
                  </>
                ) : error ? (
                  <>
                    <FileText className="w-12 h-12 mb-2 text-red-500" />
                    <span className="text-red-600 font-medium">Preview Error</span>
                    <span className="text-sm text-gray-600 mt-2 text-center">{error}</span>
                    <div className="text-xs text-gray-500 mt-2 max-w-md break-all">
                      <span className="font-medium">URL:</span> {signedUrl}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        );
      }
  
      if (type === 'text/csv') {
        // Show loading state while fetching CSV
        if (isLoadingCsv && !error) {
          return (
            <div className={fallbackClasses}>
              <TableIcon className="w-12 h-12 mb-2 animate-pulse" />
              <span>Loading CSV data...</span>
            </div>
          );
        }
        
        // Show fallback link if CSV loading failed or preview isn't available
        if (error || (!csvData && !isLoadingCsv && !isConverting)) {
          return (
            <div className={fallbackClasses}>
              <TableIcon className="w-12 h-12 mb-2 text-blue-500" />
              <span className="text-blue-600 font-medium text-center max-w-md break-words">
                {name || 'CSV File'}
              </span>
              {signedUrl && (
                <a 
                  href={signedUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  onClick={(e) => {
                    // Let the browser handle the link naturally
                    // This will either display the CSV or download it depending on browser settings
                    console.log('Opening CSV file:', signedUrl);
                  }}
                >
                  <TableIcon className="w-4 h-4 mr-2" />
                  Open CSV File
                </a>
              )}
            </div>
          );
        }
        
        // Show CSV data in table format
        if (csvData) {
          const { headers, rows } = csvData;
          const maxRows = 100; // Limit display to prevent performance issues
          const displayRows = rows.slice(0, maxRows);
          const hasMore = rows.length > maxRows;
          
          return (
            <div className="w-full p-4 overflow-auto">
              <div className="max-h-[600px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {headers.map((header, index) => (
                        <TableHead key={index} className="font-semibold">
                          {header || `Column ${index + 1}`}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayRows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <TableCell key={cellIndex} className="max-w-xs truncate">
                            {cell}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {hasMore && (
                <div className="mt-4 text-sm text-gray-500 text-center">
                  Showing first {maxRows} of {rows.length} rows
                </div>
              )}
              <div className="mt-2 text-xs text-gray-500 text-center">
                {name || 'CSV Document'} • {headers.length} columns • {rows.length} rows
              </div>
            </div>
          );
        }
        
        // Fallback - shouldn't reach here but just in case
        return (
          <div className={fallbackClasses}>
            <TableIcon className="w-12 h-12 mb-2" />
            <span>CSV file: {name || 'Document'}</span>
          </div>
        );
      }
  
      if (type.startsWith('text/')) {
        return (
          <>
            {!error ? (
              <div className="relative w-full">
                <iframe
                  src={signedUrl || 'about:blank'}
                  title={`Text Preview - ${name || 'Document'}`}
                  className="w-full h-full"
                  style={{ minHeight: '400px' }}
                  onError={(e) => {
                    console.error('Text preview iframe error:', e);
                    console.error('Failed to load URL:', signedUrl);
                    setError('Unable to load text file. The file may not exist, be corrupted, or may have access restrictions.');
                  }}
                  onLoad={(e) => {
                    try {
                      const iframe = e.target as HTMLIFrameElement;
                      const doc = iframe.contentDocument || iframe.contentWindow?.document;
                      if (!doc || doc.body.innerHTML.includes('blocked')) {
                        setError('Content blocked by security policy. Please contact support@aitana.chat for assistance.');
                      }
                    } catch (err) {
                      console.error('Text preview access error:', err);
                      if (err instanceof DOMException && err.name === 'SecurityError') {
                        setError('This file cannot be embedded due to security restrictions. The file may be configured to prevent embedding in iframes.');
                      } else {
                        setError('Unable to display text preview. Please contact support@aitana.chat for assistance.');
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <div className={fallbackClasses}>
                <FileText className="w-12 h-12 mb-2 text-red-500" />
                <span className="text-red-600 font-medium">Preview Error</span>
                <span className="text-sm text-gray-600 mt-2 text-center">{error}</span>
                <div className="text-xs text-gray-500 mt-2 max-w-md break-all">
                  <span className="font-medium">URL:</span> {signedUrl}
                </div>
              </div>
            )}
          </>
        );
      }
  
      // Default fallback
      return (
        <div className={fallbackClasses}>
          <FileIcon className="w-12 h-12 mb-2" />
          <span>{name || 'File'}</span>
        </div>
      );
    };
  
    // Main render
    return (
      <div className={containerClasses}>
        {renderContent()}
      </div>
    );
  });

PreviewContent.displayName = 'PreviewContent';

const Preview = memo((props: PreviewProps) => {
    const [api, setApi] = React.useState<CarouselApi>();
    const [current, setCurrent] = React.useState(0);
    
    const items = useMemo(() => {
      if (typeof props.items === 'string') {
        try {
          const parsedItems = JSON.parse(props.items) as PreviewItem[];
          // Validate the parsed items
          if (!Array.isArray(parsedItems)) return [];
          return parsedItems.filter((item): item is PreviewItem => (
            typeof item === 'object' &&
            item !== null &&
            typeof item.uri === 'string' &&
            (item.uri.toLowerCase().startsWith('http') || item.uri.toLowerCase().startsWith('gs://')) &&
            typeof item.type === 'string'
          ));
        } catch (e) {
          console.error('Failed to parse items JSON:', e);
          return [];
        }
      }
      if (Array.isArray(props.items)) {
        return props.items;
      }
      // Support uri, src, and url props
      const uri = props.uri || props.src || props.url;
      if (uri && props.type) {
        return [{
          uri: uri,
          type: props.type,
          name: props.name
        }];
      }
      if (uri) {
        // Auto-detect type from file extension if not provided
        const extension = uri.split('.').pop()?.toLowerCase();
        let detectedType = '';
        
        if (extension) {
          if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
            detectedType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
          } else if (['pdf'].includes(extension)) {
            detectedType = 'application/pdf';
          } else if (['mp4', 'webm', 'ogg'].includes(extension)) {
            detectedType = `video/${extension}`;
          } else if (['mp3', 'wav', 'ogg'].includes(extension)) {
            detectedType = `audio/${extension}`;
          } else if (['txt', 'md'].includes(extension)) {
            detectedType = `text/${extension === 'md' ? 'markdown' : 'plain'}`;
          } else if (extension === 'csv') {
            detectedType = 'text/csv';
          }
        }
        
        if (detectedType) {
          return [{
            uri: uri,
            type: detectedType,
            name: props.name
          }];
        }
      }
      return [];
    }, [props.items, props.uri, props.src, props.url, props.type, props.name]);
  
    const { className } = props;

    // Setup carousel API and current slide tracking
    React.useEffect(() => {
        if (!api) return;

        api.on("select", () => {
        setCurrent(api.selectedScrollSnap());
        });
    }, [api]);
  
    if (!items.length) {
      return (
        <>
          <br />
          <span className="inline-block text-red-500">
            Preview requires either uri and type props or an items array
          </span>
          <br />
        </>
      );
    }
  
    if (items.length === 1) {
      return (
        <>
          <br />
          <PreviewContent {...items[0]} className={className} />
          <br />
        </>
      );
    }
  
    return (
      <>
        <br />
        <span className="inline-block w-full">
          <Carousel
            setApi={setApi}
            orientation="vertical"
            opts={{
              align: "start",
              loop: true,
            }}
            className={twMerge("w-full", className)}
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {items.map((item: PreviewItem, index: number) => (
                <CarouselItem 
                  key={`${item.uri}-${index}`}
                  className="pl-2 md:pl-4 basis-full"
                >
                  <PreviewContent {...item} className="h-full" />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </span>
        <br />
      </>
    );
}, (prevProps: PreviewProps, nextProps: PreviewProps) => {
    // Handle string items
    if (typeof prevProps.items === 'string' || typeof nextProps.items === 'string') {
      try {
        const prevItems = typeof prevProps.items === 'string' ? 
          JSON.parse(prevProps.items) as PreviewItem[] : 
          (prevProps.items || []);
        const nextItems = typeof nextProps.items === 'string' ? 
          JSON.parse(nextProps.items) as PreviewItem[] : 
          (nextProps.items || []);
        
        if (!Array.isArray(prevItems) || !Array.isArray(nextItems)) return false;
        if (prevItems.length !== nextItems.length) return false;
        
        return prevItems.every((item, index) => {
          const nextItem = nextItems[index];
          if (!nextItem) return false;
          return item.uri === nextItem.uri &&
                 item.type === nextItem.type &&
                 item.name === nextItem.name;
        });
      } catch (e) {
        return false;
      }
    }
  
    // Handle array items
    if (Array.isArray(prevProps.items) || Array.isArray(nextProps.items)) {
      const prevItems = prevProps.items || [];
      const nextItems = nextProps.items || [];
      
      if (prevItems.length !== nextItems.length) return false;
      return prevItems.every((item, index) => {
        const nextItem = nextItems[index];
        if (!nextItem) return false;
        return item.uri === nextItem.uri &&
               item.type === nextItem.type &&
               item.name === nextItem.name;
      });
    }
  
    // Handle single item props
    return prevProps.uri === nextProps.uri &&
           prevProps.src === nextProps.src &&
           prevProps.url === nextProps.url &&
           prevProps.type === nextProps.type &&
           prevProps.name === nextProps.name &&
           prevProps.className === nextProps.className;
  });
  
  Preview.displayName = 'Preview';
  
  // Export with proper typing for the markdown component system
  export const PreviewComponent: React.ComponentType<BaseCustomProps> = Preview as React.ComponentType<BaseCustomProps>;